import { getPollOptionLabels } from './model/pollOptions'
import { FrequencyType } from 'components/NewsFeed/PollTaskForm/utils'
import { isUserIncludedInGroups } from 'components/NewsFeed/PostModal/utils'
import { IConfigStateGroups, ITag } from 'constants/interfaces'
import keys from 'constants/keys'
import dayjs from 'dayjs'
import { Content, Subtask } from './FormStepContext'
import { CHART_COLORS } from 'utils/taskAnalytics'
import { EditorContent } from 'components/CommonComponents/RichTextEditor'

export const DEFAULT_ALLOWED_OVERDUE_DAYS = 7

export enum PRIORITY_VALUES {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  NONE = -1,
}

export const PRIORITY_OPTIONS = [
  {
    label: 'low',
    value: PRIORITY_VALUES.LOW,
    bgColor: CHART_COLORS.done,
    textColor: CHART_COLORS.doneText,
  },
  {
    label: 'medium',
    value: PRIORITY_VALUES.MEDIUM,
    bgColor: CHART_COLORS.inProgress,
    textColor: CHART_COLORS.inProgressText,
  },
  {
    label: 'high',
    value: PRIORITY_VALUES.HIGH,
    bgColor: CHART_COLORS.rejected,
    textColor: CHART_COLORS.rejectedText,
  },
  {
    label: 'none',
    value: PRIORITY_VALUES.NONE,
    bgColor: CHART_COLORS.todo,
    textColor: CHART_COLORS.todoText,
  },
]

type RecurrenceDeadlineArgs = {
  ts: string
  frequencyTime: string
}
const bindTimeToDate = ({ ts, frequencyTime }: RecurrenceDeadlineArgs): string => {
  const baseDate = dayjs(ts)

  const [hours, minutes] = frequencyTime.split(':').map(Number)

  let recurrenceDeadline = baseDate.set('hour', hours).set('minute', minutes).set('second', 0).set('millisecond', 0)

  // If the resulting time is before the original ts, add a day
  if (recurrenceDeadline.isBefore(baseDate)) {
    recurrenceDeadline = recurrenceDeadline.add(1, 'day')
  }

  return recurrenceDeadline.format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
}

type CalculateRecurrenceDeadlineArgs = {
  is_recurring: boolean
  recurrence_frequency: number | null
  from_ts: string
  frequency_time: string
  monthly_range: string[]
  days_of_week: number[] | null
}
export const calculateRecurrenceDeadline = ({
  is_recurring,
  recurrence_frequency,
  from_ts,
  frequency_time,
  monthly_range,
  days_of_week,
}: CalculateRecurrenceDeadlineArgs) => {
  if (!is_recurring) {
    return null
  }

  if (recurrence_frequency === FrequencyType.DAY) {
    return bindTimeToDate({ ts: from_ts, frequencyTime: frequency_time })
  }

  if (recurrence_frequency === FrequencyType.MONTH) {
    return bindTimeToDate({ ts: monthly_range[1], frequencyTime: frequency_time })
  }

  if (recurrence_frequency === FrequencyType.WEEK) {
    const [startRange, endRange] = days_of_week ?? []
    const daysDifference = endRange - startRange
    const daysToAdd = daysDifference >= 0 ? daysDifference : 7 + daysDifference

    const endDay = dayjs(from_ts).add(daysToAdd, 'day')
    return bindTimeToDate({ ts: endDay.toISOString(), frequencyTime: frequency_time })
  }
}

type CalculateDaysOfWeek = {
  recurrence_frequency: number | null
  days_of_week: number[] | null
}
export const calculateDaysOfWeek = ({
  recurrence_frequency,
  days_of_week,
}: CalculateDaysOfWeek): number[] | number | null => {
  if (!days_of_week) {
    return null
  }
  if (recurrence_frequency === FrequencyType.DAY) {
    return days_of_week?.length === 7 ? null : days_of_week
  }
  if (recurrence_frequency === FrequencyType.WEEK) {
    return [days_of_week[0]]
  }
  return null
}

type CalculateTaskUsersArgs = {
  users: string[]
  groups: string[]
  tags: string[]
  uid: string
  configTags: ITag[]
  treeGroups: IConfigStateGroups
}

export const calculateTaskUsers = ({
  users,
  groups,
  tags,
  uid,
  configTags,
  treeGroups,
}: CalculateTaskUsersArgs): string[] => {
  const isCreatorInGroups = isUserIncludedInGroups(uid, groups, treeGroups)

  const checkIsCreatorInTags = () => {
    if (!tags.length) {
      return false
    }

    const selectedTags = configTags.filter((tag) => tags.includes(tag.sk))
    const mergedTagsGroups = Array.from(new Set(selectedTags.map((tag) => tag?.groups ?? []).flat()))

    const mergedTagsUsers = Array.from(new Set(selectedTags.map((tag) => tag?.users ?? []).flat()))

    return isUserIncludedInGroups(uid, mergedTagsGroups, treeGroups) || mergedTagsUsers.includes(uid)
  }

  const isCreatorInTags = checkIsCreatorInTags()

  return isCreatorInGroups || isCreatorInTags ? users : Array.from(new Set([...users, uid]))
}

type TotsArgs = {
  is_recurring: boolean
  deadline: string | null
  formattedDeadline: string
}
export const calculateTots = ({ is_recurring, deadline, formattedDeadline }: TotsArgs) => {
  return is_recurring
    ? formattedDeadline.toString()
    : dayjs(deadline).add(7, 'day').format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
}

type RecurrenceFrequencyArgs = {
  frequency: number | null
  days_of_week: number[] | null
}
export const calculateRecurrenceFrequency = ({ frequency, days_of_week }: RecurrenceFrequencyArgs) => {
  if (frequency === FrequencyType.DAY && days_of_week && days_of_week?.length < 7) {
    return FrequencyType.WEEK
  }

  return frequency
}

type SubtaskOutput = {
  title: string
  type: number
  category?: string
  external_link?: string
  is_multi_choice?: boolean
  options?: string[]
  is_numeric?: boolean
  is_external_status_update?: boolean
  is_required?: boolean
  content?: {
    text?: string
    rich_text?: EditorContent
    images?: string[]
    videos?: string[]
    files?: string[]
  }
}

export type SubtaskWithNumeric = Subtask & { is_numeric?: boolean; is_external_status_update?: boolean } & {
  is_external_status_update?: boolean
}

export const transformSubtasks = (input: (SubtaskWithNumeric | Content)[]): (SubtaskOutput | Content)[] => {
  return input.map((subtask) => {
    if ('index_ref' in subtask) {
      return subtask as Content
    }

    const {
      title,
      type,
      category,
      external_link,
      is_multi_choice,
      options,
      is_numeric,
      is_required,
      is_external_status_update,
      content,
    } = subtask

    const transformed: SubtaskOutput = { title, type, is_required, content, ...(category ? { category } : {}) }

    if (external_link) {
      transformed.external_link = external_link
      transformed.is_external_status_update = is_external_status_update
    }

    if (type === keys.SUBTASK_TYPES.POLL_SUBTASK || type === keys.SUBTASK_TYPES.PRIVATE_POLL) {
      transformed.is_multi_choice = is_multi_choice ?? false
      transformed.options = getPollOptionLabels(options)
    }

    if (type === keys.SUBTASK_TYPES.OPEN_QUESTION) {
      transformed.is_numeric = is_numeric
    }
    return transformed
  })
}

/**
 * Locale keys are served from the backend and `useLocaleText` returns the key itself when it is missing.
 * Use this for keys that have not been added there yet so the UI shows readable text meanwhile.
 */
export const localeFallback = (translated: string, key: string, fallback: string): string =>
  translated === key ? fallback : translated

/**
 * Titles a freshly added media subtask starts with — locale KEYS, translated for display by
 * `useTranslateDefaultTitle`. Defined here (not in the modal footer that appends the subtasks) so both
 * creation flows and `utils` itself can use them without an import cycle.
 */
export const DEFAULT_T_TAKE_A_PHOTO = 't_take_a_photo'
export const DEFAULT_T_RECORD_A_VIDEO = 't_record_a_video'

/**
 * True while the subtask still carries its untouched default title — treat that as "no title yet",
 * e.g. when seeding the AI steps, so the widget shows its own translated placeholder, not the raw key.
 */
export const isDefaultSubtaskTitle = (title?: string): boolean =>
  !title || title === DEFAULT_T_TAKE_A_PHOTO || title === DEFAULT_T_RECORD_A_VIDEO
